/**
 * ITER295 — Operations Excellence · Admin Tickets Inbox
 *
 * Central inbox for every operational ticket in the tenant. Three tabs:
 *   • Tickets     — the working list with filters
 *   • Failure Reg — audit trail of every checklist failure
 *   • Escalate    — one-click digest send (Phase-2 scheduler lands in ITER296)
 *
 * The list + detail flow is deliberately compact. Managers get the same
 * data at `/manager/tickets` with the outlet auto-scoped.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Filter, RefreshCw, Ticket as TicketIcon,
  Send, ChevronRight, Plus, AlertTriangle, Clock, CheckCircle2,
  Building2, X, MessageSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { SectionBand, KpiCard as SharedKpi } from '../components/ops/SectionBand';
import BackToDashboard from '../components/ops/BackToDashboard';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS  = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];

const STATUS_STYLE = {
  open:        { bg: '#FEF3C7', fg: '#B45309', label: 'Open' },
  in_progress: { bg: '#DBEAFE', fg: '#1D4ED8', label: 'In Progress' },
  resolved:    { bg: '#D1FAE5', fg: '#065F46', label: 'Resolved' },
  closed:      { bg: '#E5E7EB', fg: '#374151', label: 'Closed' },
};
const PRIORITY_STYLE = {
  low:      { bg: '#F3F4F6', fg: '#4B5563', label: 'Low' },
  medium:   { bg: '#E0E7FF', fg: '#3730A3', label: 'Medium' },
  high:     { bg: '#FED7AA', fg: '#9A3412', label: 'High' },
  critical: { bg: '#FECACA', fg: '#991B1B', label: 'Critical' },
};

function Pill({ style, children, testId }) {
  return (
    <span data-testid={testId}
          className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold
                     rounded-full uppercase tracking-[0.08em]"
          style={{ background: style.bg, color: style.fg }}>
      {children}
    </span>
  );
}

function fmtAge(hrs) {
  if (hrs == null) return '—';
  if (hrs < 1)   return `${Math.round(hrs * 60)}m`;
  if (hrs < 24)  return `${hrs.toFixed(1)}h`;
  return `${Math.round(hrs / 24)}d`;
}

// ==================================================================
// Ticket list row
// ==================================================================
function TicketRow({ t, onOpen }) {
  const s = STATUS_STYLE[t.status] || STATUS_STYLE.open;
  const p = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
  const isRoom = t.source_type === 'room_checklist_failure';
  const isManual = t.source_type === 'manual';
  return (
    <tr onClick={() => onOpen(t)}
        data-testid={`ticket-row-${t.ticket_id}`}
        className="cursor-pointer hover:bg-slate-50 border-b last:border-b-0">
      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{t.ticket_id}</td>
      <td className="px-3 py-2.5 text-sm text-slate-800">
        <div className="font-medium truncate max-w-[280px]">{t.title}</div>
        <div className="text-[11px] text-slate-500 truncate max-w-[280px]">
          {t.description}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {isRoom && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                         bg-indigo-50 text-indigo-800 border border-indigo-200"
              data-testid={`room-source-badge-${t.ticket_id}`}
            >
              ROOM {t.room_number || '—'}
            </span>
          )}
          {isManual && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                         bg-slate-100 text-slate-700 border border-slate-200"
            >
              MANUAL
            </span>
          )}
          {!isRoom && !isManual && t.source_type === 'checklist_failure' && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold
                         bg-amber-50 text-amber-800 border border-amber-200"
            >
              CHECKLIST
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
        {t.outlet_name || '—'}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
        {t.department}
      </td>
      <td className="px-3 py-2.5"><Pill style={p}>{p.label}</Pill></td>
      <td className="px-3 py-2.5">
        <Pill style={s}>{s.label}</Pill>
        {t.overdue && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 ml-1">
            <AlertTriangle className="w-2.5 h-2.5" /> OVERDUE
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">
        {fmtAge(t.age_hours)}
      </td>
      <td className="px-3 py-2.5 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
    </tr>
  );
}

// ==================================================================
// Ticket detail modal — state transitions + resolution notes
// ==================================================================
function TicketDetailModal({ ticket, onClose, onSaved, departments }) {
  const { getHeaders } = useAuth();
  const [local, setLocal] = useState(ticket);
  const [notes, setNotes] = useState(ticket.resolution_notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(ticket);
    setNotes(ticket.resolution_notes || '');
  }, [ticket]);

  const patch = async (payload, closeAfter = false) => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/api/tickets/${ticket.id}`, payload, { headers: getHeaders() },
      );
      setLocal(res.data);
      toast.success('Ticket updated');
      onSaved?.(res.data);
      if (closeAfter) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const s = STATUS_STYLE[local.status] || STATUS_STYLE.open;
  const p = PRIORITY_STYLE[local.priority] || PRIORITY_STYLE.medium;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="ticket-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-mono text-xs text-slate-500 mb-1">
                {local.ticket_id}
              </div>
              <DialogTitle className="text-lg leading-tight">
                {local.title}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Pill style={p}>{p.label}</Pill>
              <Pill style={s}>{s.label}</Pill>
              {local.overdue && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px]
                                 font-semibold text-red-700 bg-red-50 rounded-full">
                  <AlertTriangle className="w-3 h-3" /> OVERDUE
                </span>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {local.description && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">
                Description
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {local.description}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">
                Outlet
              </div>
              <div className="text-sm text-slate-800">{local.outlet_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">
                Department
              </div>
              <Select
                value={local.department}
                onValueChange={(v) => patch({ department: v })}
                disabled={saving}
              >
                <SelectTrigger data-testid="ticket-department-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">
                Priority
              </div>
              <Select
                value={local.priority}
                onValueChange={(v) => patch({ priority: v })}
                disabled={saving}
              >
                <SelectTrigger data-testid="ticket-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p2) => (
                    <SelectItem key={p2} value={p2}>{PRIORITY_STYLE[p2].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C6B64] mb-0.5">
                Age · SLA · Time Remaining
              </div>
              <div className="text-sm text-[#1A2421]">
                {fmtAge(local.age_hours)} · {local.sla_hours}h ·{' '}
                <span className={local.time_remaining_hours < 0 ? 'text-red-700 font-semibold' : 'text-[color:var(--color-primary)]'}
                      data-testid="ticket-sla-remaining">
                  {local.time_remaining_hours == null ? '—'
                    : local.time_remaining_hours < 0
                      ? `${Math.abs(local.time_remaining_hours).toFixed(1)}h overdue`
                      : `${local.time_remaining_hours.toFixed(1)}h left`}
                </span>
              </div>
            </div>
          </div>

          {local.source_type === 'checklist_failure' && local.source_ref && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-800 mb-1">
                Source · Checklist Failure
              </div>
              <div className="text-xs text-amber-900">
                {local.source_ref.checklist_department || '—'} checklist ·
                {' '}{local.source_ref.date}
              </div>
            </div>
          )}

          {local.source_type === 'room_checklist_failure' && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3"
                 data-testid="room-source-panel">
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-indigo-800 mb-1">
                Source · Room Checklist Failure
              </div>
              <div className="text-xs text-indigo-900 grid grid-cols-2 gap-x-4 gap-y-1">
                <div>
                  <span className="font-semibold">Room:</span>{' '}
                  {local.room_number || local.source_ref?.room_number || '—'}
                </div>
                {(local.room_type || local.source_ref?.room_type) && (
                  <div>
                    <span className="font-semibold">Type:</span>{' '}
                    {local.room_type || local.source_ref?.room_type}
                  </div>
                )}
                <div className="col-span-2">
                  <span className="font-semibold">Checklist:</span>{' '}
                  {local.checklist_name || local.source_ref?.checklist_name || 'Room Checklist'}
                  {' · '}{local.source_ref?.date}
                </div>
              </div>
            </div>
          )}

          {local.resolution_notes && (
            <div className="rounded-md border border-[#E2E8DF] bg-[#F9F9F8] p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C6B64] mb-1">
                Resolution Notes
              </div>
              <div className="text-sm text-[#1A2421] whitespace-pre-wrap">
                {local.resolution_notes}
              </div>
            </div>
          )}

          {Array.isArray(local.resolution_photos) && local.resolution_photos.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C6B64] mb-1">
                Resolution Photos ({local.resolution_photos.length})
              </div>
              <div className="flex flex-wrap gap-2" data-testid="resolution-photos-admin">
                {local.resolution_photos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer"
                     className="block w-20 h-20 rounded border border-[#E2E8DF] overflow-hidden hover:opacity-80">
                    <img src={url} alt={`Resolution ${i + 1}`}
                         className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C6B64]">
                Activity Log
              </div>
              <div className="text-[10px] text-[#5C6B64]">
                {(local.activity_log || []).length} entr{(local.activity_log || []).length === 1 ? 'y' : 'ies'}
              </div>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(local.activity_log || []).length === 0 ? (
                <div className="text-xs text-[#5C6B64] italic">
                  No activity yet — use &quot;Request Update&quot; below to nudge the responsible team.
                </div>
              ) : (
                (local.activity_log || []).slice().reverse().map((a) => (
                  <div key={a.id} className="text-xs bg-[#F9F9F8] rounded border border-[#E2E8DF] p-2"
                       data-testid={`activity-entry-${a.id}`}>
                    <div className="flex items-center justify-between text-[10px] text-[#5C6B64] mb-0.5">
                      <span className="font-semibold uppercase tracking-wider">
                        {a.by_name || a.by_email} · {a.by_role}
                      </span>
                      <span>{new Date(a.at).toLocaleString()}</span>
                    </div>
                    <div className="text-[#1A2421] whitespace-pre-wrap">{a.message}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="border-t border-[#E2E8DF] pt-3">
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C6B64] mb-1">
              Request Update
            </div>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note or nudge the responsible team."
              data-testid="ticket-request-update-input"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-between sm:justify-between">
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              disabled={saving || !notes.trim()}
              data-testid="ticket-request-update-btn"
              onClick={async () => {
                if (!notes.trim()) return;
                try {
                  setSaving(true);
                  const res = await axios.post(
                    `${API}/api/tickets/${local.id}/nudge`,
                    { message: notes.trim() },
                    { headers: getHeaders() },
                  );
                  const nextLog = [...(local.activity_log || []), res.data];
                  setLocal((prev) => ({ ...prev, activity_log: nextLog }));
                  setNotes('');
                  toast.success('Update request sent to the assigned team');
                  onSaved?.({ ...local, activity_log: nextLog });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to send request');
                } finally {
                  setSaving(false);
                }
              }}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
            >
              <MessageSquare className="w-4 h-4 mr-1" /> Request Update
            </Button>
            <div className="text-[10px] text-[#5C6B64] max-w-xs">
              Admins reassign &amp; nudge. Resolve/Close is performed by the responsible team.
            </div>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================================================================
// Manual ticket create
// ==================================================================
function ManualTicketModal({ onClose, onCreated, outlets, departments }) {
  const { getHeaders } = useAuth();
  const [form, setForm] = useState({
    outlet_id: outlets[0]?.id || '',
    department: departments[0] || 'Maintenance',
    priority: 'medium',
    sla_hours: 24,
    title: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.outlet_id || !form.title.trim() || !form.department) {
      toast.error('Outlet, title and department are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/tickets`, {
        ...form, sla_hours: Number(form.sla_hours) || 24,
      }, { headers: getHeaders() });
      toast.success(`Ticket ${res.data.ticket_id} created`);
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="manual-ticket-modal">
        <DialogHeader><DialogTitle>New Operational Ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Outlet</Label>
              <Select value={form.outlet_id}
                      onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                <SelectTrigger data-testid="mt-outlet"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={form.department}
                      onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="mt-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Brief summary of the issue"
                   data-testid="mt-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="Location, severity, what has been tried already…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>{PRIORITY_STYLE[p].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>SLA (hours)</Label>
              <Input type="number" min={1} max={720} value={form.sla_hours}
                     onChange={(e) => setForm({ ...form, sla_hours: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mt-save">
            {saving ? 'Creating…' : 'Create Ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================================================================
// Failure Register tab
// ==================================================================
function FailureRegisterView({ outlets, departments, initialOutlet }) {
  const { getHeaders } = useAuth();
  const [outletId, setOutletId] = useState(initialOutlet || '');
  const [dept, setDept] = useState('');
  const [source, setSource] = useState('');            // ITER296.1
  const [roomNumber, setRoomNumber] = useState('');    // ITER296.1
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ days: '90', limit: '500' });
      if (outletId)   p.set('outlet_id', outletId);
      if (dept)       p.set('department', dept);
      if (source)     p.set('source', source);
      if (roomNumber) p.set('room_number', roomNumber);
      const res = await axios.get(`${API}/api/tickets/failure-register?${p}`,
                                   { headers: getHeaders() });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load register');
    } finally { setLoading(false); }
  }, [outletId, dept, source, roomNumber, getHeaders]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <Select value={outletId || 'all'}
                onValueChange={(v) => setOutletId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-56" data-testid="freg-outlet">
            <SelectValue placeholder="All outlets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outlets</SelectItem>
            {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dept || 'all'}
                onValueChange={(v) => setDept(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All depts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source || 'all'}
                onValueChange={(v) => { setSource(v === 'all' ? '' : v); if (v !== 'room_checklist_failure') setRoomNumber(''); }}>
          <SelectTrigger className="w-44" data-testid="freg-source">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="checklist_failure">Department Checklist</SelectItem>
            <SelectItem value="room_checklist_failure">Room Checklist</SelectItem>
            <SelectItem value="manual">Manual Ticket</SelectItem>
          </SelectContent>
        </Select>
        {source === 'room_checklist_failure' && (
          <Input
            className="w-28"
            placeholder="Room #"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            data-testid="freg-room-number"
          />
        )}
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="rounded-md border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="failure-register-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Outlet</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Room</th>
              <th className="px-3 py-2">Checklist Dept</th>
              <th className="px-3 py-2">Failed Question</th>
              <th className="px-3 py-2">Remark</th>
              <th className="px-3 py-2">Ticket</th>
              <th className="px-3 py-2">Assigned To</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Resolution (h)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">
                No failures in the selected window.
              </td></tr>
            ) : rows.map((r) => {
              const src = r.source || 'checklist_failure';
              const srcLabel = src === 'room_checklist_failure' ? 'Room'
                : src === 'manual' ? 'Manual' : 'Dept';
              const srcStyle = src === 'room_checklist_failure'
                ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                : src === 'manual'
                  ? 'bg-slate-100 text-slate-700 border-slate-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200';
              return (
                <tr key={r.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2 text-xs text-slate-600">{r.date}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.outlet_name}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${srcStyle}`}>
                      {srcLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-700">{r.room_number || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.department}</td>
                  <td className="px-3 py-2 text-xs text-slate-800">{r.checklist_task_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[240px] truncate">
                    {r.remark || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{r.ticket_id}</td>
                  <td className="px-3 py-2 text-xs">{r.assigned_department}</td>
                  <td className="px-3 py-2 text-xs">{(r.status || '').replace('_', ' ')}</td>
                  <td className="px-3 py-2 text-xs">{r.resolution_time_hours ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================================================================
// Escalation panel — one-off "send now" digest
// ==================================================================
function EscalationPanel({ outlets }) {
  const { getHeaders } = useAuth();
  const [form, setForm] = useState({
    recipients: '',
    outlet_id: '',
    priority: '',
    only_overdue: false,
  });
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!form.recipients.trim()) {
      toast.error('Enter at least one recipient');
      return;
    }
    setSending(true);
    try {
      const p = new URLSearchParams({ recipients: form.recipients.trim() });
      if (form.outlet_id) p.set('outlet_id', form.outlet_id);
      if (form.priority)  p.set('priority', form.priority);
      if (form.only_overdue) p.set('only_overdue', 'true');
      const res = await axios.post(
        `${API}/api/admin/tickets/escalation/send-now?${p}`,
        {}, { headers: getHeaders() },
      );
      if (res.data.sent) {
        toast.success(`Digest sent — ${res.data.ticket_count} tickets to ${res.data.recipients.length} recipients`);
      } else if (res.data.reason === 'no-tickets') {
        toast.info('No matching tickets. Nothing to send.');
      } else {
        toast.warning('Send returned without confirmation — check server logs');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-xl bg-white rounded-lg border border-[#E2E8DF] p-6" data-testid="escalation-panel">
      <div className="flex items-center gap-2 mb-1">
        <Send className="w-4 h-4 text-slate-500" />
        <div className="text-sm font-semibold text-slate-800">Send Escalation Digest</div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        A single tabular email covering matching open tickets. Recipients configured
        as scheduled rules will land in the next iteration.
      </p>

      <div className="space-y-3">
        <div>
          <Label>Recipients (comma-separated)</Label>
          <Input value={form.recipients}
                 onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                 placeholder="ops@example.com, gm@example.com"
                 data-testid="esc-recipients" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Outlet (optional)</Label>
            <Select value={form.outlet_id || 'all'}
                    onValueChange={(v) => setForm({ ...form, outlet_id: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="All outlets" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outlets</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority filter</Label>
            <Select value={form.priority || 'any'}
                    onValueChange={(v) => setForm({ ...form, priority: v === 'any' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="critical">Critical only</SelectItem>
                <SelectItem value="high,critical">High + Critical</SelectItem>
                <SelectItem value="medium,high,critical">Medium+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.only_overdue}
                 onChange={(e) => setForm({ ...form, only_overdue: e.target.checked })} />
          Overdue tickets only
        </label>
        <div className="pt-2">
          <Button onClick={send} disabled={sending} data-testid="esc-send">
            <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send Now'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// Main page
// ==================================================================
export default function AdminTickets() {
  const { getHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  // ITER300 · Read URL query params on mount so KPI-drill-throughs land
  // with the correct filter state preserved. Everything else (Refresh,
  // manual filter edits) stays local state — the URL is a one-way seed.
  const [tab, setTab] = useState(
    () => searchParams.get('tab') || 'tickets',
  );
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [departments, setDepartments] = useState(['Maintenance']);
  const [filters, setFilters] = useState(() => ({
    outlet_id:   searchParams.get('outlet_id')   || '',
    department:  searchParams.get('department')  || '',
    status:      searchParams.get('status')      || 'open,in_progress',
    priority:    searchParams.get('priority')    || '',
    source:      searchParams.get('source')      || '',
    room_number: searchParams.get('room_number') || '',
    overdue:     searchParams.get('overdue') === 'true',
    q:           searchParams.get('q')           || '',
  }));
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.outlet_id) p.set('outlet_id', filters.outlet_id);
      if (filters.department) p.set('department', filters.department);
      if (filters.status)    p.set('status', filters.status);
      if (filters.priority)  p.set('priority', filters.priority);
      if (filters.source)    p.set('source', filters.source);
      if (filters.room_number) p.set('room_number', filters.room_number);
      if (filters.overdue)   p.set('overdue', 'true');
      const res = await axios.get(`${API}/api/tickets?${p}`, { headers: getHeaders() });
      setTickets(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tickets');
    } finally { setLoading(false); }
  }, [filters, getHeaders]);

  useEffect(() => {
    (async () => {
      try {
        const [oRes, dRes] = await Promise.all([
          axios.get(`${API}/api/outlets`, { headers: getHeaders() }),
          axios.get(`${API}/api/admin/departments`, { headers: getHeaders() }),
        ]);
        setOutlets(oRes.data || []);
        setDepartments(dRes.data?.departments || ['Maintenance']);
      } catch { /* non-fatal */ }
    })();
  }, [getHeaders]);

  useEffect(() => { if (tab === 'tickets') loadTickets(); }, [tab, loadTickets]);

  // ITER-ops-digest · Deep link from Daily Ops Digest email lands here as
  // `?ticket_id=<uuid>`. Auto-open the ticket detail modal so admins can
  // action/close it directly. We fetch from the loaded list first (fast
  // path); if not present in the current filter view, fall back to the
  // direct-fetch endpoint. The URL param is a one-shot seed — we don't
  // reopen the modal on every re-render.
  const focusedTicketId = searchParams.get('ticket_id');
  const focusHandledRef = React.useRef(null);
  useEffect(() => {
    if (!focusedTicketId || tab !== 'tickets') return;
    if (focusHandledRef.current === focusedTicketId) return;
    // Wait for tickets to load before attempting the in-list lookup.
    if (loading) return;
    (async () => {
      const inList = tickets.find((t) => t.id === focusedTicketId);
      if (inList) {
        focusHandledRef.current = focusedTicketId;
        setDetail(inList);
        return;
      }
      // Not in current filtered view — fetch directly by id.
      try {
        const r = await axios.get(`${API}/api/tickets/${focusedTicketId}`,
                                  { headers: getHeaders() });
        if (r.data) {
          focusHandledRef.current = focusedTicketId;
          setDetail(r.data);
        }
      } catch (e) {
        focusHandledRef.current = focusedTicketId;
        toast.error('Unable to open that ticket. It may have been resolved or you may not have access.');
      }
    })();
  }, [focusedTicketId, tab, tickets, loading, getHeaders]);

  const filtered = useMemo(() => {
    if (!filters.q.trim()) return tickets;
    const q = filters.q.toLowerCase();
    return tickets.filter((t) =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.ticket_id || '').toLowerCase().includes(q),
    );
  }, [tickets, filters.q]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, overdue: 0, critical: 0 };
    for (const t of tickets) {
      if (t.status === 'open')        c.open++;
      if (t.status === 'in_progress') c.in_progress++;
      if (t.overdue)                  c.overdue++;
      if (t.priority === 'critical' && (t.status === 'open' || t.status === 'in_progress'))
        c.critical++;
    }
    return c;
  }, [tickets]);

  return (
    <div className="min-h-screen bg-white" data-testid="admin-tickets-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard />
        <SectionBand
          title="Operational Tickets"
          subtitle="Every checklist failure lands here. Manual tickets can be raised for any operational issue."
          icon={TicketIcon}
          testId="admin-tickets-page-band"
          actions={
            <Button onClick={() => setCreating(true)} size="sm"
                    className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                    data-testid="new-ticket-btn">
              <Plus className="w-4 h-4 mr-1" /> New Ticket
            </Button>
          }
        />

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { k: 'Open', v: counts.open, rag: null },
            { k: 'In Progress', v: counts.in_progress, rag: null },
            { k: 'Overdue', v: counts.overdue, rag: counts.overdue > 0 ? 'poor' : null },
            { k: 'Critical Pending', v: counts.critical, rag: counts.critical > 0 ? 'warning' : null },
          ].map((k) => (
            <div key={k.k} data-testid={`kpi-${k.k.replace(' ', '-').toLowerCase()}`}>
              <SharedKpi label={k.k} value={String(k.v)} rag={k.rag} />
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-4">
          {[
            { k: 'tickets',    label: 'Tickets' },
            { k: 'failures',   label: 'Failure Register' },
            { k: 'escalation', label: 'Escalation' },
          ].map((tt) => (
            <button key={tt.k} onClick={() => setTab(tt.k)}
                    data-testid={`ticket-tab-${tt.k}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      tab === tt.k
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}>
              {tt.label}
            </button>
          ))}
        </div>

        {tab === 'tickets' && (
          <>
            {/* Filter row */}
            <div className="flex flex-wrap gap-2 items-center mb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input className="pl-8 w-64"
                       value={filters.q}
                       onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                       placeholder="Search title / ticket id"
                       data-testid="ticket-search" />
              </div>
              <Select value={filters.outlet_id || 'all'}
                      onValueChange={(v) => setFilters({ ...filters, outlet_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-48" data-testid="ticket-outlet-filter">
                  <SelectValue placeholder="All outlets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All outlets</SelectItem>
                  {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.department || 'all'}
                      onValueChange={(v) => setFilters({ ...filters, department: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-44"><SelectValue placeholder="All depts" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.status || 'any'}
                      onValueChange={(v) => setFilters({ ...filters, status: v === 'any' ? '' : v })}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any status</SelectItem>
                  <SelectItem value="open,in_progress">Active only</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.priority || 'any'}
                      onValueChange={(v) => setFilters({ ...filters, priority: v === 'any' ? '' : v })}>
                <SelectTrigger className="w-36"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any priority</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high,critical">High+Critical</SelectItem>
                  <SelectItem value="medium,high,critical">Medium+</SelectItem>
                </SelectContent>
              </Select>
              {/* ITER300 · Overdue toggle. Rendered as a filter chip so
                  clickable KPIs on the Command Centre can pre-select it
                  via `?overdue=true`. */}
              <button
                type="button"
                onClick={() => setFilters({ ...filters, overdue: !filters.overdue })}
                data-testid="ticket-overdue-filter"
                className={`inline-flex items-center gap-1 h-9 px-3 rounded-md border text-[12px] font-medium transition-colors ${
                  filters.overdue
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Overdue only
              </button>
              <Select value={filters.source || 'any'}
                      onValueChange={(v) => setFilters({ ...filters, source: v === 'any' ? '' : v, room_number: v === 'room_checklist_failure' ? filters.room_number : '' })}>
                <SelectTrigger className="w-40" data-testid="ticket-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any source</SelectItem>
                  <SelectItem value="checklist_failure">Department Checklist</SelectItem>
                  <SelectItem value="room_checklist_failure">Room Checklist</SelectItem>
                  <SelectItem value="manual">Manually raised</SelectItem>
                </SelectContent>
              </Select>
              {filters.source === 'room_checklist_failure' && (
                <Input
                  className="w-32"
                  placeholder="Room #"
                  value={filters.room_number}
                  onChange={(e) => setFilters({ ...filters, room_number: e.target.value })}
                  data-testid="ticket-room-number-filter"
                />
              )}
              <Button variant="outline" size="sm" onClick={loadTickets}
                       data-testid="ticket-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>

            {/* Ticket table */}
            <div className="rounded-md border bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="ticket-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-3 py-2">Ticket</th>
                    <th className="px-3 py-2">Title</th>
                    <th className="px-3 py-2">Outlet</th>
                    <th className="px-3 py-2">Dept</th>
                    <th className="px-3 py-2">Priority</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Age</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                      <TicketIcon className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                      No tickets match. Try relaxing the filters or raise a new ticket.
                    </td></tr>
                  ) : filtered.map((t) => (
                    <TicketRow key={t.id} t={t} onOpen={setDetail} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'failures' && (
          <FailureRegisterView outlets={outlets} departments={departments}
                                initialOutlet={filters.outlet_id} />
        )}

        {tab === 'escalation' && <EscalationPanel outlets={outlets} />}
      </div>

      {detail && (
        <TicketDetailModal
          ticket={detail}
          departments={departments}
          onClose={() => setDetail(null)}
          onSaved={(t) => {
            setTickets((prev) => prev.map((x) => x.id === t.id ? t : x));
            setDetail(t);
          }}
        />
      )}
      {creating && (
        <ManualTicketModal
          outlets={outlets}
          departments={departments}
          onClose={() => setCreating(false)}
          onCreated={() => loadTickets()}
        />
      )}
    </div>
  );
}
